import config from "../configs/config";

const REQUEST_TIMEOUT_MS = 60000; // 60 s; override per call via options.timeout

class ApiService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
    this.token = null;
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem("auth_token", token);
    } else {
      localStorage.removeItem("auth_token");
    }
  }

  getToken() {
    if (!this.token) {
      this.token = localStorage.getItem("auth_token");
    }
    return this.token;
  }

  clearAuth() {
    this.token = null;
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
    localStorage.removeItem("organization");
  }

  async request(endpoint, options = {}) {
    const token = this.getToken();
    const isFormData = options.body instanceof FormData;
    const timeout = options.timeout ?? REQUEST_TIMEOUT_MS;

    const headers = {
      // Omit Content-Type for FormData — browser sets it with the correct boundary
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...options.headers,
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // Remove internal-only option before passing to fetch
    const { timeout: _t, ...fetchOptions } = options;

    const controller = new AbortController();
    const timerId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : null;

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...fetchOptions,
        headers,
        signal: controller.signal,
      });

      if (response.status === 401) {
        this.clearAuth();
        window.location.href = "/login";
        throw new Error("Session expired. Please log in again.");
      }

      const contentType = response.headers.get("content-type");
      const data =
        contentType && contentType.includes("application/json")
          ? await response.json()
          : await response.text();

      if (!response.ok) {
        throw new Error(
          (typeof data === "object" && data?.message) || data || "Request failed"
        );
      }

      return { status: response.status, data };
    } catch (error) {
      if (error.name === "AbortError") {
        throw new Error("Request timed out. Please try again.");
      }
      throw error;
    } finally {
      if (timerId) clearTimeout(timerId);
    }
  }

  // ── JSON helpers ──────────────────────────────────────────────────────────

  async get(endpoint, options = {}) {
    return this.request(endpoint, { method: "GET", ...options });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "POST",
      body: JSON.stringify(data),
      ...options,
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: "PUT",
      body: JSON.stringify(data),
      ...options,
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { method: "DELETE", ...options });
  }

  // ── File / multipart upload helper ────────────────────────────────────────
  // Pass a pre-built FormData object; auth header is added automatically.
  async upload(endpoint, formData, options = {}) {
    if (!(formData instanceof FormData)) {
      throw new Error("upload() requires a FormData instance");
    }
    return this.request(endpoint, {
      method: "POST",
      body: formData,
      timeout: options.timeout ?? 300000, // 5-minute default for uploads
      ...options,
    });
  }

  // ── Auth-specific convenience methods ────────────────────────────────────

  async loginWithMicrosoft(idToken) {
    return this.post("/loginOffice", { id_token: idToken });
  }

  async logout() {
    try {
      const response = await this.post("/logout", {});
      this.clearAuth();
      return response;
    } catch (error) {
      this.clearAuth();
      throw error;
    }
  }
}

const apiService = new ApiService();
export default apiService;
